import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Skeleton,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Eye,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Pencil,
  Trash2
} from 'lucide-react';
import {
  getTradePayables,
  recordPayment,
  getPayablesSummary,
  deletePayable,
  updatePaymentRecord,
  deletePaymentRecord
} from '../firebase/tradePayablesService';
import {
  debitCashAccount,
  creditCashAccount,
  getAccountBalance
} from '../firebase/cashBankService';

const PAY_FROM_OPTIONS = [
  { value: 'cash_in_hand',       label: 'Cash in Hand',                method: 'cash'   },
  { value: 'cash_at_bank',       label: 'Cash at Bank (Bank Transfer)', method: 'bank'   },
  { value: 'petty_cash',         label: 'Petty Cash',                  method: 'cash'   },
  { value: 'cash_at_bank_cheque',label: 'Cheque (Cash at Bank)',        method: 'cheque' },
];

const PAYMENT_METHOD_LABEL = {
  cash: 'Cash', bank: 'Bank Transfer', cheque: 'Cheque'
};

const TradePayables = ({ currentUser, permissions }) => {
  const [payables, setPayables]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [tabValue, setTabValue]               = useState(0);
  const [summary, setSummary]                 = useState(null);
  const [snackbar, setSnackbar]               = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting]           = useState(false);

  // ── Selected payable ──────────────────────────────────────────────────────
  const [selectedPayable, setSelectedPayable] = useState(null);

  // ── View dialog ───────────────────────────────────────────────────────────
  const [viewDialog, setViewDialog]           = useState(false);

  // ── Edit payable (header fields) ──────────────────────────────────────────

  // ── Delete payable ────────────────────────────────────────────────────────
  const [deletePayableDialog, setDeletePayableDialog] = useState(false);

  // ── Record new payment ────────────────────────────────────────────────────
  const [paymentDialog, setPaymentDialog]     = useState(false);
  const [paymentAmount, setPaymentAmount]     = useState('');
  const [payFrom, setPayFrom]                 = useState('cash_in_hand');
  const [reference, setReference]             = useState('');
  const [paymentNotes, setPaymentNotes]       = useState('');
  const [accountBalance, setAccountBalance]   = useState(null);
  const [showOverdraftWarning, setShowOverdraftWarning] = useState(false);

  // ── Edit payment record ───────────────────────────────────────────────────
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [selectedPayment, setSelectedPayment]     = useState(null);
  const [editPaymentForm, setEditPaymentForm]     = useState({
    amount: '', payFrom: 'cash_in_hand', reference: '', notes: ''
  });
  const [editPaymentBalance, setEditPaymentBalance] = useState(null);

  // ── Delete payment record ─────────────────────────────────────────────────
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [payablesResult, summaryResult] = await Promise.all([
      getTradePayables(),
      getPayablesSummary()
    ]);
    if (payablesResult.success) setPayables(payablesResult.data);
    if (summaryResult.success) setSummary(summaryResult.data);
    setLoading(false);
  };

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── View ──────────────────────────────────────────────────────────────────
  const handleViewPayable = (payable) => {
    setSelectedPayable(payable);
    setViewDialog(true);
  };

  // ── Delete payable ────────────────────────────────────────────────────────
  const handleConfirmDeletePayable = async () => {
    setSubmitting(true);
    const result = await deletePayable(selectedPayable.id, currentUser);
    setSubmitting(false);
    if (result.success) {
      showSnack('Payable deleted successfully');
      setDeletePayableDialog(false);
      setViewDialog(false);
      loadData();
    } else {
      showSnack(result.error, 'error');
    }
  };

  // ── Record new payment ────────────────────────────────────────────────────
  const handleOpenPaymentDialog = (payable) => {
    setSelectedPayable(payable);
    setPaymentAmount(payable.balanceAmount.toString());
    setPayFrom('cash_in_hand');
    setReference('');
    setPaymentNotes('');
    setShowOverdraftWarning(false);
    getAccountBalance('cash_in_hand').then(r => setAccountBalance(r.balance ?? null));
    setPaymentDialog(true);
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      showSnack('Please enter a valid payment amount', 'error'); return;
    }
    if (parseFloat(paymentAmount) > selectedPayable.balanceAmount) {
      showSnack('Payment amount cannot exceed balance amount', 'error'); return;
    }
    if (!showOverdraftWarning && accountBalance !== null && parseFloat(paymentAmount) > accountBalance) {
      setShowOverdraftWarning(true); return;
    }

    setShowOverdraftWarning(false);
    setSubmitting(true);

    const selectedOption = PAY_FROM_OPTIONS.find(o => o.value === payFrom) || PAY_FROM_OPTIONS[0];
    const accountType   = selectedOption.value.replace('_cheque', '');
    const paymentMethod = selectedOption.method;

    const result = await recordPayment(
      selectedPayable.id,
      { amount: parseFloat(paymentAmount), paymentMethod, accountType, reference, notes: paymentNotes },
      currentUser
    );
    setSubmitting(false);

    if (result.success) {
      debitCashAccount(
        accountType, parseFloat(paymentAmount), selectedPayable.id, 'trade_payable',
        `Supplier Payment - ${selectedPayable.supplierName}`, paymentMethod, null, currentUser
      ).catch(err => console.error('Cash ledger debit failed:', err));
      showSnack('Payment recorded successfully');
      setPaymentDialog(false);
      // Refresh selectedPayable inside view dialog
      const fresh = await getTradePayables();
      if (fresh.success) {
        setPayables(fresh.data);
        const updated = fresh.data.find(p => p.id === selectedPayable.id);
        if (updated) setSelectedPayable(updated);
      }
    } else {
      showSnack(result.error, 'error');
    }
  };

  // ── Edit payment record ───────────────────────────────────────────────────
  const handleOpenEditPayment = (payment) => {
    setSelectedPayment(payment);
    // Map accountType back to the payFrom option
    const matchedOption = PAY_FROM_OPTIONS.find(o =>
      o.value === payment.accountType || o.value.replace('_cheque', '') === payment.accountType
    );
    const payFromValue = matchedOption ? matchedOption.value : 'cash_in_hand';
    setEditPaymentForm({
      amount:    payment.amount.toString(),
      payFrom:   payFromValue,
      reference: payment.reference || '',
      notes:     payment.notes || ''
    });
    getAccountBalance(payment.accountType).then(r => setEditPaymentBalance(r.balance ?? null));
    setEditPaymentDialog(true);
  };

  const handleSaveEditPayment = async () => {
    const newAmount = parseFloat(editPaymentForm.amount);
    if (isNaN(newAmount) || newAmount <= 0) {
      showSnack('Please enter a valid payment amount', 'error'); return;
    }

    // Ensure new amount doesn't exceed totalAmount across all payments
    const otherPaymentsTotal = (selectedPayable.payments || [])
      .filter(p => p.id !== selectedPayment.id)
      .reduce((sum, p) => sum + p.amount, 0);
    if (otherPaymentsTotal + newAmount > selectedPayable.totalAmount) {
      showSnack('Total payments would exceed the payable amount', 'error'); return;
    }

    setSubmitting(true);
    const selectedOption = PAY_FROM_OPTIONS.find(o => o.value === editPaymentForm.payFrom) || PAY_FROM_OPTIONS[0];
    const accountType   = selectedOption.value.replace('_cheque', '');
    const paymentMethod = selectedOption.method;

    const result = await updatePaymentRecord(
      selectedPayable.id,
      selectedPayment.id,
      { amount: newAmount, paymentMethod, accountType, reference: editPaymentForm.reference, notes: editPaymentForm.notes },
      currentUser,
      { creditCashAccount, debitCashAccount }
    );
    setSubmitting(false);

    if (result.success) {
      showSnack('Payment updated successfully');
      setEditPaymentDialog(false);
      // Refresh view dialog data
      const fresh = await getTradePayables();
      if (fresh.success) {
        setPayables(fresh.data);
        const updated = fresh.data.find(p => p.id === selectedPayable.id);
        if (updated) setSelectedPayable(updated);
      }
    } else {
      showSnack(result.error, 'error');
    }
  };

  // ── Delete payment record ─────────────────────────────────────────────────
  const handleOpenDeletePayment = (payment) => {
    setSelectedPayment(payment);
    setDeletePaymentDialog(true);
  };

  const handleConfirmDeletePayment = async () => {
    setSubmitting(true);
    const result = await deletePaymentRecord(
      selectedPayable.id,
      selectedPayment.id,
      currentUser,
      { creditCashAccount }
    );
    setSubmitting(false);

    if (result.success) {
      showSnack('Payment deleted and cash ledger reversed');
      setDeletePaymentDialog(false);
      const fresh = await getTradePayables();
      if (fresh.success) {
        setPayables(fresh.data);
        const updated = fresh.data.find(p => p.id === selectedPayable.id);
        if (updated) setSelectedPayable(updated);
      }
    } else {
      showSnack(result.error, 'error');
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':        return 'warning';
      case 'partially_paid': return 'info';
      case 'paid':           return 'success';
      case 'overdue':        return 'error';
      default:               return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':        return 'Pending';
      case 'partially_paid': return 'Partially Paid';
      case 'paid':           return 'Paid';
      case 'overdue':        return 'Overdue';
      default:               return status;
    }
  };

  const getFilteredPayables = () => {
    switch (tabValue) {
      case 1: return payables.filter(p => p.status === 'pending' || p.status === 'partially_paid');
      case 2: return payables.filter(p => p.status === 'overdue');
      case 3: return payables.filter(p => p.status === 'paid');
      default: return payables;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  const filteredPayables = getFilteredPayables();

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Trade Payables
      </Typography>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DollarSign size={20} color="#f57c00" />
                  <Typography color="text.secondary">Total Outstanding</Typography>
                </Box>
                <Typography variant="h4" color="error.main">
                  LKR {summary.totalOutstanding.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AlertCircle size={20} color="#d32f2f" />
                  <Typography color="text.secondary">Overdue</Typography>
                </Box>
                <Typography variant="h4" color="error.main">
                  LKR {summary.totalOverdue.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Clock size={20} color="#1976d2" />
                  <Typography color="text.secondary">Due This Week</Typography>
                </Box>
                <Typography variant="h4" color="primary.main">
                  LKR {summary.dueThisWeek.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircle size={20} color="#2e7d32" />
                  <Typography color="text.secondary">Pending Payables</Typography>
                </Box>
                <Typography variant="h4">{summary.pendingCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`All (${payables.length})`} />
          <Tab label={`Pending (${payables.filter(p => p.status === 'pending' || p.status === 'partially_paid').length})`} />
          <Tab label={`Overdue (${payables.filter(p => p.status === 'overdue').length})`} />
          <Tab label={`Paid (${payables.filter(p => p.status === 'paid').length})`} />
        </Tabs>
      </Paper>

      {/* Payables Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Supplier</TableCell>
              <TableCell>GRN #</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell align="right">Paid Amount</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayables.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No payables found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPayables.map((payable) => (
                <TableRow
                  key={payable.id}
                  hover
                  sx={{ backgroundColor: payable.status === 'overdue' ? 'error.lighter' : 'inherit' }}
                >
                  <TableCell>
                    <Typography fontWeight={500}>{payable.supplierName}</Typography>
                  </TableCell>
                  <TableCell>{payable.grnNumber || payable.poNumber || '-'}</TableCell>
                  <TableCell>{payable.invoiceNumber || '-'}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>LKR {payable.totalAmount.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell align="right">LKR {payable.paidAmount.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600} color={payable.balanceAmount > 0 ? 'error.main' : 'success.main'}>
                      LKR {payable.balanceAmount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{payable.dueDate?.toDate().toLocaleDateString() || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(payable.status)}
                      color={getStatusColor(payable.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewPayable(payable)} color="primary">
                        <Eye size={18} />
                      </IconButton>
                    </Tooltip>
                    {payable.status !== 'paid' && (
                      <Tooltip title="Record Payment">
                        <IconButton size="small" onClick={() => handleOpenPaymentDialog(payable)} color="success">
                          <DollarSign size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* VIEW PAYABLE DIALOG                                                */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Payable Details</DialogTitle>
        <DialogContent>
          {selectedPayable && (
            <Box sx={{ mt: 1 }}>
              {/* Header fields */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Supplier</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedPayable.supplierName}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">GRN #</Typography>
                  <Typography variant="body1">{selectedPayable.grnNumber || selectedPayable.poNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Invoice #</Typography>
                  <Typography variant="body1">{selectedPayable.invoiceNumber || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Payment Terms</Typography>
                  <Typography variant="body1">{selectedPayable.paymentTerms || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body1">{selectedPayable.dueDate?.toDate().toLocaleDateString() || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={getStatusLabel(selectedPayable.status)}
                    color={getStatusColor(selectedPayable.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    LKR {selectedPayable.totalAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    LKR {selectedPayable.paidAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Balance</Typography>
                  <Typography variant="body1" fontWeight={600} color="error.main">
                    LKR {selectedPayable.balanceAmount.toLocaleString()}
                  </Typography>
                </Grid>
                {selectedPayable.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedPayable.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              {/* Payment History Table */}
              {selectedPayable.payments && selectedPayable.payments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Payment History</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPayable.payments.map((payment) => (
                          <TableRow key={payment.id} hover>
                            <TableCell>
                              {payment.date?.toDate
                                ? payment.date.toDate().toLocaleDateString()
                                : new Date(payment.date).toLocaleDateString()}
                              {payment.editedAt && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  (edited)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>
                                LKR {payment.amount.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {PAYMENT_METHOD_LABEL[payment.paymentMethod] || payment.paymentMethod || '-'}
                            </TableCell>
                            <TableCell>{payment.reference || '-'}</TableCell>
                            <TableCell>{payment.notes || '-'}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit payment">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditPayment(payment)}
                                >
                                  <Pencil size={15} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete payment">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDeletePayment(payment)}
                                >
                                  <Trash2 size={15} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedPayable?.paidAmount === 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Trash2 size={16} />}
                onClick={() => setDeletePayableDialog(true)}
              >
                Delete
              </Button>
            )}
          </Box>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DELETE PAYABLE DIALOG                                              */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={deletePayableDialog} onClose={() => setDeletePayableDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Payable</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payable for{' '}
            <strong>{selectedPayable?.supplierName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This cannot be undone. Only payables with no recorded payments can be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePayableDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirmDeletePayable} variant="contained" color="error" disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* RECORD NEW PAYMENT DIALOG                                          */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedPayable && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                Balance Amount: LKR {selectedPayable.balanceAmount.toLocaleString()}
              </Alert>
              <TextField
                label="Payment Amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Pay From"
                select
                value={payFrom}
                onChange={(e) => {
                  setPayFrom(e.target.value);
                  setShowOverdraftWarning(false);
                  const acct = e.target.value.replace('_cheque', '');
                  getAccountBalance(acct).then(r => setAccountBalance(r.balance ?? null));
                }}
                fullWidth
              >
                {PAY_FROM_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              {accountBalance !== null && (
                <Alert severity={parseFloat(paymentAmount) > accountBalance ? 'warning' : 'info'} sx={{ py: 0.5 }}>
                  Available balance:{' '}
                  <strong>LKR {accountBalance.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</strong>
                  {parseFloat(paymentAmount) > accountBalance && (
                    <> —{' '}
                      <strong>
                        LKR {(parseFloat(paymentAmount) - accountBalance).toLocaleString('en-LK', { minimumFractionDigits: 2 })} short
                      </strong>
                    </>
                  )}
                </Alert>
              )}
              {showOverdraftWarning && (
                <Alert severity="warning">
                  This payment exceeds the available balance. The account will go into negative.
                  Click <strong>Record Payment</strong> again to confirm.
                </Alert>
              )}
              <TextField
                label="Reference Number"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Cheque number, transaction ID, etc."
                fullWidth
              />
              <TextField
                label="Notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={submitting}>
            {submitting ? 'Recording…' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EDIT PAYMENT RECORD DIALOG                                         */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={editPaymentDialog} onClose={() => setEditPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment Record</DialogTitle>
        <DialogContent>
          {selectedPayment && selectedPayable && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                Original amount: <strong>LKR {selectedPayment.amount.toLocaleString()}</strong>
                {' · '}
                Payable total: <strong>LKR {selectedPayable.totalAmount.toLocaleString()}</strong>
              </Alert>
              <TextField
                label="Payment Amount"
                type="number"
                value={editPaymentForm.amount}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Pay From"
                select
                value={editPaymentForm.payFrom}
                onChange={(e) => {
                  setEditPaymentForm({ ...editPaymentForm, payFrom: e.target.value });
                  const acct = e.target.value.replace('_cheque', '');
                  getAccountBalance(acct).then(r => setEditPaymentBalance(r.balance ?? null));
                }}
                fullWidth
              >
                {PAY_FROM_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              {editPaymentBalance !== null && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Current account balance:{' '}
                  <strong>LKR {editPaymentBalance.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</strong>
                </Alert>
              )}
              <TextField
                label="Reference Number"
                value={editPaymentForm.reference}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, reference: e.target.value })}
                placeholder="Cheque number, transaction ID, etc."
                fullWidth
              />
              <TextField
                label="Notes"
                value={editPaymentForm.notes}
                onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <Alert severity="warning" sx={{ py: 0.5 }}>
                The cash &amp; bank ledger will be adjusted automatically for any difference.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPaymentDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSaveEditPayment} variant="contained" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DELETE PAYMENT RECORD DIALOG                                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={deletePaymentDialog} onClose={() => setDeletePaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Payment Record</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <>
              <Typography>
                Delete the payment of{' '}
                <strong>LKR {selectedPayment.amount.toLocaleString()}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This will reverse the corresponding cash &amp; bank ledger entry and update the
                payable balance and status automatically.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePaymentDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirmDeletePayment} variant="contained" color="error" disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TradePayables;
